'use client'

import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'

import { ClevertapTextBanner } from './clevertapTextBanner'

/** CleverTap KV topic for the home page-load text banner. */
const TOPIC = 'Home_Pageload'

interface BannerData {
    title?: string
    subtitle?: string
    url?: string
    /** Raw event.detail — passed back to CleverTap for viewed/clicked events. */
    raw: unknown
}

/**
 * Logic layer for the CleverTap web-native-display (KV) home banner. Listens for
 * the `CT_web_native_display` event, renders only the `Home_Pageload` topic, and
 * raises CleverTap viewed/clicked events. Renders nothing until a matching
 * campaign arrives.
 */
const ClevertapTextBannerWrapper: React.FC = () => {
    const t = useTranslations('common')
    const [data, setData] = useState<BannerData | null>(null)

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent).detail
            const kv = detail?.kv || {}
            if (kv.topic !== TOPIC) return
            setData({ title: kv.title, subtitle: kv.subtitle, url: kv.url, raw: detail })
            ;(window as any).clevertap?.renderNotificationViewed?.(detail)
        }
        document.addEventListener('CT_web_native_display', handler)
        return () => document.removeEventListener('CT_web_native_display', handler)
    }, [])

    const handleViewDetails = useCallback(() => {
        if (!data) return
        ;(window as any).clevertap?.renderNotificationClicked?.(data.raw)
        if (data.url) window.open(data.url, '_blank', 'noopener,noreferrer')
    }, [data])

    if (!data || (!data.title && !data.subtitle)) return null

    return (
        <ClevertapTextBanner
            title={data.title ?? ''}
            subTitle={data.subtitle ?? ''}
            viewDetailsLabel={t('viewDetails')}
            showViewDetails={!!data.url}
            onViewDetails={handleViewDetails}
        />
    )
}

export { ClevertapTextBannerWrapper }
export default ClevertapTextBannerWrapper
